import { WhereItIs } from "../components/WhereItIs";
import { useBackState } from "@/hooks/useScrollMemory";
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LoadMore } from "@/components/LoadMore";
import { usePaged } from "@/hooks/usePaged";
import { formatShort } from "@/lib/datetime";
import { MapPin, Search, Loader2, ArrowRight, Download } from "lucide-react";

const fmt = (iso) => (iso ? formatShort(iso) : "—");

export default function PutawayLogPage() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, truncated: false });
  const [q, setQ] = useBackState("q", "");
  const [start, setStart] = useBackState("start", "");
  const [end, setEnd] = useBackState("end", "");
  const [loading, setLoading] = useState(true);

  const params = () => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const ps = params();
      const { data } = await api.get(`/putaway/log${ps ? `?${ps}` : ""}`);
      setRows(data.items || []);
      setMeta({ total: data.total ?? (data.items || []).length, truncated: !!data.truncated });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const { visible, hasMore, more, shownCount, total } = usePaged(rows, 50);

  const exportCsv = async () => {
    try {
      const ps = params();
      const { data } = await api.get(`/putaway/log?${ps}${ps ? "&" : ""}format=csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "putaway-log.csv"; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed."); }
  };

  return (
    <div data-testid="putaway-log-page" className="space-y-5 max-w-5xl">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><MapPin className="w-5 h-5 text-[#B01E28]" />Parts Put Away</h1>
        <p className="text-sm text-slate-500 mt-1">Every part put-away — who put which part where, and when. Read directly from the append-only ledger.</p>
        {/* HONEST ABOUT WHAT IT ISN'T (his instruction 2026-10-08). This log has always read
            part.putaway only; pallet/crate/tote moves live in the movement ledger. One place for
            every move — parts, containers and trucks — is in the history-screens review. */}
        <p data-testid="putaway-log-scope-note" className="text-sm text-slate-500 mt-1">
          Pallet, crate and tote moves are in <a href="/moves-history" className="underline font-semibold">Moves History</a>.
        </p>
      </div>

      <form onSubmit={onSearch} className="flex flex-wrap gap-2 items-end">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="putaway-log-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search part, bin, or person…" className="pl-8" autoComplete="off" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">From</label>
          <Input type="date" data-testid="putaway-log-start" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">To</label>
          <Input type="date" data-testid="putaway-log-end" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" />
        </div>
        <button type="submit" data-testid="putaway-log-search-btn" className="h-10 px-4 rounded-lg bg-[#1F1F1F] text-white text-sm">Search</button>
        <Button type="button" variant="outline" data-testid="putaway-log-export" onClick={exportCsv} className="h-10 rounded-lg border-slate-300">
          <Download className="w-4 h-4 mr-1.5" />Export CSV
        </Button>
      </form>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          <div className="col-span-3">When</div>
          <div className="col-span-3">Part</div>
          <div className="col-span-3">From → To bin</div>
          <div className="col-span-3">By</div>
        </div>
        {loading ? (
          <div className="p-12 text-center font-mono text-xs text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />LOADING…</div>
        ) : rows.length === 0 ? (
          <div data-testid="putaway-log-empty" className="p-12 text-center text-sm text-slate-400">No putaway actions recorded yet.</div>
        ) : visible.map((r) => (
          <div key={r.id} data-testid={`putaway-log-row-${r.tx_number}`}
            className="block md:grid md:grid-cols-12 gap-2 px-5 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 text-sm">
            <div className="md:col-span-3 font-mono text-xs text-slate-500">{fmt(r.timestamp)}</div>
            <div className="md:col-span-3">
              {r.part?.id ? (
                <Link to={`/parts/${r.part.id}`} className="text-[#E0781A] hover:underline font-medium">{r.part?.code || r.part.id}</Link>
              ) : <span className="text-slate-400">—</span>}
              {r.part?.name && <span className="text-slate-500 ml-1 hidden md:inline">· {r.part.name}</span>}
            </div>
            <div className="md:col-span-3 font-mono text-xs flex items-center gap-1.5 flex-wrap">
              <WhereItIs location={r.from_location} size="sm" showCode={false} icon={false} fallback="—" testid="putaway-log-from" />
              <ArrowRight className="w-3 h-3 text-slate-300" />
              <WhereItIs location={r.to_location} size="sm" showCode={false} icon={false} fallback="—" testid="putaway-log-to" />
            </div>
            <div className="md:col-span-3 text-xs text-slate-600 truncate">{r.user_email}</div>
          </div>
        ))}
      </div>
      {!loading && meta.truncated && (
        <div data-testid="putaway-log-truncated" className="mt-2 text-[11px] text-slate-500 font-mono text-center">
          Showing the newest {rows.length} of {meta.total} matching actions — narrow the dates or refine the search to see the rest.
        </div>
      )}
      {!loading && rows.length > 0 && (
        <LoadMore shownCount={shownCount} total={total} hasMore={hasMore} onMore={more} noun="action" testid="putaway-log-load-more" />
      )}
    </div>
  );
}
